'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

/** Wait before loading a preview, so sweeping the mouse across the grid doesn't fetch every clip. */
const HOVER_DELAY_MS = 150;

/**
 * Hover state for a clip tile. Mouse only: touch and pen taps open the viewer
 * as before. Off when the user prefers reduced motion.
 */
export function useClipHoverPreview() {
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clear = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  };

  useEffect(() => clear, []);

  const bind = useCallback((clipId: string) => ({
    onPointerEnter: (e: React.PointerEvent) => {
      if (e.pointerType !== 'mouse') return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      clear();
      timer.current = setTimeout(() => setHoveredId(clipId), HOVER_DELAY_MS);
    },
    onPointerLeave: () => {
      clear();
      setHoveredId((cur) => (cur === clipId ? null : cur));
    },
  }), []);

  return { hoveredId, bind };
}

/**
 * Muted, looping video laid over the thumbnail while the tile is hovered. It
 * only mounts while hovered, and fades in once frames are playing, so the
 * thumbnail stays visible while it loads.
 */
export function ClipHoverPreview({ src, className = 'object-cover' }: { src: string; className?: string }) {
  const ref = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    const video = ref.current;
    if (!video) return;
    // Set here, not as a prop, so the cleanup below can always undo it.
    video.muted = true; // autoplay policy: must be muted before play()
    video.src = src;
    video.play().catch(() => {});
    return () => {
      // Stop the download when the mouse leaves.
      video.pause();
      video.removeAttribute('src');
      video.load();
    };
  }, [src]);

  return (
    <video
      ref={ref}
      muted
      loop
      playsInline
      preload="auto"
      disablePictureInPicture
      aria-hidden
      tabIndex={-1}
      onPlaying={() => setPlaying(true)}
      className={`pointer-events-none absolute inset-0 h-full w-full transition-opacity duration-200 ${className} ${playing ? 'opacity-100' : 'opacity-0'}`}
    />
  );
}
